import { Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useCoreStore } from '../core/store';
import { AppCoreEnums } from '../../core/shared/enums';
import { IPagedList, Pagination } from '../core/models';
import { httpClient } from '../../core/axios/http-client';
import { ResolveParams, ResolveResult } from '../../core/models/routing';
import { IUsersState, UserGroups, User, UserAvatar, UserLite, SaveSessionUserInfoRequest, ProfileLite } from './models';
import updatePaginationStateData from '../../core/composable/use-pagination-utils';
import useContainerFiltersResolveParams from '../../core/composable/containers/use-container-filters-resolve-params';

const useUsersStoreActions = (state: Ref<IUsersState>) => {
  const route = useRoute();
  const coreStore = useCoreStore();
  const { buildResolveParamsFiltersUsingId } = useContainerFiltersResolveParams();

  const loadUsers = (payload: ResolveParams) => {
    const filters = buildResolveParamsFiltersUsingId(payload);

    return httpClient.post<void, IPagedList<User>>('users', filters).then((response) => {
      state.value.users = response.dataList;
      return new ResolveResult(filters.pagination, response, payload);
    });
  };

  const loadAllUsersLite = async () => {
    const response = await httpClient.get<void, UserLite[]>(`users/all-users-lite`);
    state.value.allUsersLite = response;
  };

  const loadAvailableProfiles = async () => {
    const response = await httpClient.get<void, ProfileLite[]>(`users/available-profiles`);
    state.value.availableProfiles = response;
  };

  const loadUserById = (resolveParams: ResolveParams) => {
    return httpClient.get<void, User>(`users/${resolveParams.params.id}`).then((response) => {
      state.value.userOverview = response as User;
      state.value.userProperties = response as User;
    });
  };

  const saveUser = (payload: User) => {
    return httpClient.post<void, User>('users/save', payload).then((response) => {
      state.value.userOverview = response as User;
      state.value.userProperties = response as User;
      return response;
    });
  };

  const loadUserGroups = (payload: ResolveParams) => {
    const filters = buildResolveParamsFiltersUsingId(payload);
    return httpClient.post<void, IPagedList<UserGroups>>(`users/user-groups-page`, filters).then((response) => {
      state.value.usergroups = response.dataList;
      return new ResolveResult(filters.pagination, response, payload);
    });
  };

  const loadUserAvatar = (payload: ResolveParams) => {
    return httpClient.get<void, UserAvatar>(`users/${payload.params.id}/avatar`).then((response) => {
      state.value.userAvatar = response;
    });
  };

  const saveUserAvatar = (payload: UserAvatar) => {
    const idUser = route.params.id;
    if (!idUser || +idUser === 0) return new Promise<UserAvatar | undefined>((resolve) => resolve(undefined));

    return httpClient.post<void, UserAvatar>(`users/${idUser}/avatar`, payload).then((response) => {
      state.value.userAvatar = response;
      return response;
    });
  };

  const deleteUser = (id: number) => {
    return httpClient.post('users/delete', [id]).then(() => {
      state.value.userOverview = {} as User;
      state.value.userProperties = {} as User;
    });
  };

  const loadUserAvailableGroups = (payload: ResolveParams) => {
    const filters = buildResolveParamsFiltersUsingId(payload);

    return httpClient.post<void, IPagedList<UserGroups>>('users/available-groups', filters).then((response) => {
      state.value.availableUserGroups = response.dataList;

      updatePaginationStateData(
        { ...filters.pagination, rowsNumber: response.totalRecords } as Pagination,
        AppCoreEnums.ContainerType.DrawerContainer
      );
    });
  };

  const removeGroupsFromUser = () => {
    const idUser = route.params.id;
    const idGroups = coreStore.tableContainerData.selectedKeys;
    return httpClient.post(`users/${idUser}/remove/groups`, idGroups);
  };

  const assignGroupsToUser = () => {
    const idUser = route.params.id;
    const idGroups = coreStore.drawerContainerData.selectedKeys;
    return httpClient.post(`users/${idUser}/associate/groups`, idGroups);
  };

  const saveSessionUserInfo = (payload: SaveSessionUserInfoRequest) => {
    return httpClient.post<void, User>('users/save-session-user-info', payload).then((response) => response);
  };

  return {
    saveUser,
    loadUsers,
    loadAllUsersLite,
    loadAvailableProfiles,
    deleteUser,
    loadUserById,
    loadUserGroups,
    loadUserAvatar,
    saveUserAvatar,
    loadUserAvailableGroups,
    assignGroupsToUser,
    removeGroupsFromUser,
    saveSessionUserInfo,
    // deleteSelectedUsers,
  };
};

export default useUsersStoreActions;
